/* eslint-disable @next/next/no-img-element */

import {
  Banner,
  BannerSlider,
  BannerSliderImage,
  BannerSliderSlideItem,
  UnstyledLink
} from '@cash-rewards/cr-ui-components';
import {
  ARRAY_ELEMENT_ID,
  ELEMENT_POSITION,
  EVENT_NAME
} from '@cash-rewards/mparticlejs';
import { extractUtmQuery } from '@cash-rewards/reactjs';
import {
  secondaryBannerProductMapper,
  useCarouselClickTracking,
  useCarouselImpressionTracking
} from '@cash-rewards/reactjs/client';
import { useRouter } from 'next/router';
import { useCallback, useState } from 'react';

import '@cash-rewards/cr-ui-components/slider.css';

import useIsDesktopSize from '@/hooks/useIsDesktopSize';
import useIsTabletSize from '@/hooks/useIsTabletSize';

import ImpressionPixel from '@/modules/shop/components/ImpressionPixel';
import { SecondaryBanner } from '@/modules/shop/models/offerModels';
interface SecondaryBannersProps {
  banners: SecondaryBanner[];
}

const SecondaryBanners = ({ banners }: SecondaryBannersProps) => {
  const router = useRouter();
  const isDesktopSize = useIsDesktopSize();
  const isTabletSize = useIsTabletSize();
  const isMobile = !isTabletSize && !isDesktopSize;
  const [, setActiveBannerIndex] = useState(0);

  const attributeOverrides = {
    ...extractUtmQuery(router.query),
    element_position: ELEMENT_POSITION.bottom
  };

  const { logEvent } = useCarouselClickTracking(
    ARRAY_ELEMENT_ID.banner,
    [secondaryBannerProductMapper],
    [],
    attributeOverrides,
    EVENT_NAME.click_web_banner,
    { device: isMobile ? 'mobile' : 'desktop' }
  );

  const { onMounted } = useCarouselImpressionTracking(
    ARRAY_ELEMENT_ID.banner,
    banners,
    [secondaryBannerProductMapper],
    attributeOverrides,
    EVENT_NAME.impression_web_banner_render,
    { device: isMobile ? 'mobile' : 'desktop' }
  );

  const handleOnMounted = useCallback(() => {
    const bannersIndexList: number[] = Array.from(Array(banners.length).keys());
    onMounted(bannersIndexList);
  }, [onMounted, banners]);

  return (
    <div>
      {banners.map((banner) => {
        return (
          <ImpressionPixel
            key={banner.title}
            impressionUrl={banner.impressionUrl}
          />
        );
      })}

      <BannerSlider
        onSlideChange={(number) => {
          setActiveBannerIndex(number);
        }}
        totalSlides={banners.length}
        autoPlay={true}
        rewind={true}
        onMounted={handleOnMounted}
      >
        {banners.map((banner, idx) => {
          const {
            bannerTitle,
            termsAndConditions,
            merchantLogoOverride,
            merchantLogoUrl,
            highlightCopyInPurpleBold,
            subCopy,
            highlightCopyInPurpleLight,
            title,
            contentUrl,
            mobile,
            desktop,
            merchantName,
            merchantCommissionString,
            singleTierEndDate,
            cashbackCap,
            colorVariant
          } = banner || {};
          return (
            <BannerSliderSlideItem key={banner.title} index={idx}>
              <UnstyledLink
                onClick={() => {
                  const currentPage = idx + 1;
                  logEvent(idx, banner, currentPage, 1);
                }}
                href={banner.clickUrl}
                className='w-full'
                data-test-id={`secondary-banner-${idx}`}
              >
                {isDynamicAdBanner(banner) && (
                  <Banner
                    bannerTitle={bannerTitle || (merchantName ?? '')}
                    data-test-id={`dynamic-secondary-banner-${idx}`}
                    terms={termsAndConditions || 'Limited time. T&Cs apply.'}
                    logoUrl={merchantLogoOverride || merchantLogoUrl}
                    imageSrc={
                      isMobile
                        ? `${contentUrl}/${mobile}`
                        : `${contentUrl}/${desktop}`
                    }
                    commissionString={
                      highlightCopyInPurpleBold ||
                      (merchantCommissionString ?? '')
                    }
                    couponCode={subCopy}
                    singleTierEndDate={singleTierEndDate}
                    hideCodeLabel={true}
                    wasRate={highlightCopyInPurpleLight || cashbackCap}
                    colorVariant={colorVariant}
                  />
                )}
                {!isDynamicAdBanner(banner) && (
                  <BannerSliderImage
                    alt={`${title} banner`}
                    src={
                      isMobile
                        ? `${contentUrl}/${mobile}`
                        : `${contentUrl}/${desktop}`
                    }
                    loading='lazy'
                  ></BannerSliderImage>
                )}
              </UnstyledLink>
            </BannerSliderSlideItem>
          );
        })}
      </BannerSlider>
    </div>
  );
};

export default SecondaryBanners;

const isDynamicAdBanner = (banner: SecondaryBanner) => {
  return banner.isDynamic === true;
};
